import { useCurrentSession } from '@ferreri/minato-auth-react';
import { createPersistedStore } from '@ferreri/minato-react-utils';
import {
  alert,
  ArticleOutlineMediumIcon,
  BadgeOutlineMediumIcon,
  BookServicesOutlineMediumIcon,
  ContractOutlineMediumIcon,
  ImportantDevicesOutlineMediumIcon,
  LocalActivityOutlineMediumIcon,
  MysteryFillMediumIcon,
  PaymentsOutlineMediumIcon,
  PreverAngelusFillMediumIcon,
  ProductClubeFillMediumIcon,
  ProductPosthumousFillMediumIcon,
  ProductSalePlansOutlineMediumIcon,
  ReceiptLongOutlineMediumIcon,
  SupervisorAccountOutlineMediumIcon,
  token,
  UrnOutlineMediumIcon,
} from '@ferreri/prever-component-lib';
import { ElementType, useEffect, useMemo, useRef, useState } from 'react';

import useClubeRoutes from 'clubePublication/useHubRoutes';
import useAllContractsAndRolesCardMembership from 'contractLegacy/useLegacyAllContractsAndRolesCardMembership';
import usePlanRoutes from 'contractLegacy/useLegacyHubRoutes';
import useMostViewedTelevetRoutes from 'televet/useMostViewedTelevetRoutes';
import { useMostViewedMedicalRoutes } from 'medical/stores';
import usePosthumousRoutes from '../posthumous/posthumousRoutes';

export const useFormattedClubeRoutes = () => {
  const query = useClubeRoutes();
  const color = token.colors.solidClubePrimaryPure;

  const product = {
    link: query?.home,
    icon: ProductClubeFillMediumIcon,
    label: 'Clube de Vantagens',
    color,
  };

  const items = useMemo(() => {
    const map = {
      cinema: {
        icon: LocalActivityOutlineMediumIcon,
        label: 'Desconto em cinemas',
      },
    };

    return Object.entries(query || {}).reduce((array, [key, link]) => {
      if (Object.keys(map).includes(key)) {
        array.push({
          link,
          ...map[key],
          color,
        });
      }
      return array;
    }, []);
  }, [query]);

  return { product, items, isLoading: query?.isLoading };
};

export const useFormattedPlanRoutes = () => {
  const query = usePlanRoutes();
  const color = token.colors.neutralXxDark;

  const items = useMemo(() => {
    const map = {
      'Pagar boleto (2ᵃ via)': {
        icon: ReceiptLongOutlineMediumIcon,
        label: 'Pagar boleto (2ᵃ via)',
      },
      Carteirinha: {
        icon: BadgeOutlineMediumIcon,
        label: 'Carteirinha',
      },
      Dependentes: {
        icon: SupervisorAccountOutlineMediumIcon,
        label: 'Dependentes',
      },
      Mensalidades: {
        icon: ReceiptLongOutlineMediumIcon,
        label: 'Mensalidades',
      },
      'Manual de assistências': {
        icon: BookServicesOutlineMediumIcon,
        label: 'Manual de assistências',
      },
      Contrato: {
        icon: ContractOutlineMediumIcon,
        label: 'Contrato',
      },
      'Lista de benefícios': {
        icon: ArticleOutlineMediumIcon,
        label: 'Lista de benefícios',
      },
      'Adquirir novo plano': {
        icon: ProductSalePlansOutlineMediumIcon,
        label: 'Adquirir novo plano',
      },
      'Indique e ganhe': {
        icon: PaymentsOutlineMediumIcon,
        label: 'Indique e ganhe',
        tag: 'NOVO',
      },
    };

    return Object.entries(query || {}).reduce((array, [key, link]) => {
      if (Object.keys(map).includes(key)) {
        array.push({ link, ...map[key], color });
      }
      return array;
    }, []);
  }, [query]);

  const product = {
    icon: PreverAngelusFillMediumIcon,
    label: 'Meu Plano',
    link: query?.home,
    color,
  };

  return {
    hasContract: Boolean(query?.home),
    items,
    product,
    routes: query,
    isLoading: query?.isLoading,
  };
};

export const useFormattedPosthumousRoutes = () => {
  const query = usePosthumousRoutes();
  const color = token.colors.solidPostumosPrimaryPure;

  const product = {
    link: query?.home,
    icon: ProductPosthumousFillMediumIcon,
    label: 'Serviços Funerários',
    color,
  };

  const items = useMemo(() => {
    const map = {
      'Consulta de Falecimento': {
        icon: MysteryFillMediumIcon,
        label: 'Consulta de falecimento',
      },
      'Velório Online': {
        icon: ImportantDevicesOutlineMediumIcon,
        label: 'Velório online',
      },
      Crematório: { icon: UrnOutlineMediumIcon, label: 'Crematório' },
    };

    return Object.entries(query || {}).reduce((array, [key, link]) => {
      if (Object.keys(map).includes(key)) {
        array.push({
          link,
          ...map[key],
          color,
        });
      }
      return array;
    }, []);
  }, [query]);

  return { product, items, isLoading: query?.isLoading };
};

export const useMostViewedRoutes = () => {
  const healthItems = useMostViewedMedicalRoutes();
  const televetItems = useMostViewedTelevetRoutes();
  const { items: clubeItems } = useFormattedClubeRoutes();
  const { items: posthumousItems } = useFormattedPosthumousRoutes();
  const { items: planItems } = useFormattedPlanRoutes();

  const items = useMemo<
    Array<{
      label: string;
      color: string;
      link: string;
      icon: ElementType<{ color: string }>;
      isLoading?: boolean;
    }>
  >(() => {
    const sourceMap = new Map(
      [
        ...healthItems,
        ...televetItems,
        ...clubeItems,
        ...posthumousItems,
        ...planItems,
      ].map((item) => [item.label, item]),
    );

    const desiredOrder = [
      'Agendamento de consulta',
      'Teleconsulta',
      'Assistência Pet',
      'Orçamento de exames laboratoriais',
      'Guia médico',
      'Pagar boleto (2ᵃ via)',
      'Teleconsulta Pet',
      'Carteirinha',
      'Consulta de falecimento',
    ];

    return desiredOrder.reduce((acc, config) => {
      const itemMatched = sourceMap.get(config);
      if (itemMatched) {
        acc.push(itemMatched);
      }

      return acc;
    }, [] as typeof healthItems);
  }, [healthItems, televetItems, clubeItems, posthumousItems, planItems]);

  return { items };
};

export const usePersistedLastViewedCardPlanName = () =>
  createPersistedStore<string>('last_viewed_card_plan_name');

export const useCurrentCardPlanName = () => {
  const [cardIndex] = usePersistedLastViewedCardPlanName();

  return cardIndex;
};

export const useSetPersistedLastViewedCardPlanName = () => {
  const [, setValue] = usePersistedLastViewedCardPlanName();

  return setValue;
};

export const useCardMembershipContracts = () => {
  const { hasContract, isLoading } = useFormattedPlanRoutes();
  const query = useAllContractsAndRolesCardMembership();

  const persistedPlanName = useCurrentCardPlanName();
  const setPersistedPlanName = useSetPersistedLastViewedCardPlanName();

  const initialIndex = query?.contractsWithRoles?.findIndex(
    (contract) =>
      `${contract?.plan_group_name}-${contract?.customer_code}` ===
      persistedPlanName,
  );
  const [selectedIndex, setSelectedIndex] = useState(() =>
    initialIndex !== -1 ? initialIndex : 0,
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!query?.contractsWithRoles) return;

    if (selectedIndex >= query?.contractsWithRoles?.length) {
      setSelectedIndex(0);
    }
  }, [query?.contractsWithRoles, selectedIndex]);

  useEffect(() => {
    if (!query?.contractsWithRoles || query?.contractsWithRoles?.length === 0)
      return;

    const selected = query?.contractsWithRoles[selectedIndex];
    if (selected?.plan_group_name && selected?.customer_code) {
      const compoundKey = `${selected.plan_group_name}-${selected.customer_code}`;
      setPersistedPlanName(compoundKey);
    }
  }, [selectedIndex, query?.contractsWithRoles]);

  const selectedContract = query?.contractsWithRoles[selectedIndex];
  const handleGoToDependents = selectedContract
    ? `${window.location.origin}/contracts-legacy/${selectedContract?.seq_control}/dependents`
    : '';

  const shouldShowDependentsButton = selectedContract?.role === 'holder';
  const hasMultipleContracts = query?.contractsWithRoles?.length > 1;

  const handleSelectContract = (index: number) => {
    setSelectedIndex(index);
    setIsOpen(false);
  };

  return {
    hasContract,
    isLoading,
    contractsWithRoles: query?.contractsWithRoles,
    selectedIndex,
    selectedContract,
    handleGoToDependents,
    shouldShowDependentsButton,
    hasMultipleContracts,
    isOpen,
    setIsOpen,
    handleSelectContract,
  };
};

export const useSocialLoginAlert = () => {
  const { data: session, isLoading } = useCurrentSession();
  const trigger = useRef(false);

  useEffect(() => {
    const currentDate = new Date().getTime();
    const sessionDate = new Date(
      session?.authentication_methods.find(
        (authenticationMethod) => authenticationMethod.method === 'password',
      )?.completed_at ?? '',
    ).getTime();
    const isNewSession = currentDate - sessionDate <= 1000 * 60 * 2; // 2 segundos

    const hasOIDCLink =
      session?.authentication_methods?.length === 2 &&
      session?.authentication_methods?.every(
        (authenticationMethod) =>
          authenticationMethod.method === 'password' ||
          authenticationMethod.method === 'oidc',
      ) &&
      isNewSession;

    if (!isLoading && hasOIDCLink && trigger.current === false) {
      alert.successTimed(
        'Conta vinculada',
        'Sua conta foi vinculada ao login social com sucesso!',
      );
      trigger.current = true;
    }
  }, [session, isLoading]);
};
